import type {
  ElementMissingBehavior,
  TooltipWalkthroughStep,
} from '@unifyapps/defs/types/page/walkthrough';
import { BlockDataAttributes } from '../../hooks/useBlockDataAttributes';
import type { WalkthroughManagerStore } from '../../stores/WalkthroughManagerStore';
import { resolveElementSelector } from '../utils/resolveElementSelector';
import { scrollToElement } from '../utils/scrollToElement';

const RETRY_DELAY_BUILDER_MS = 100; // Fast retries in builder for snappy UX
const RETRY_DELAY_RUNNER_MS = 300; // Slower retries in runner

// ============================================================================
// Pure Helper Functions
// ============================================================================

/**
 * Resolves target element using step configuration with fallback chain
 */
function resolveTarget(
  step: Pick<
    TooltipWalkthroughStep,
    'targetBlockId' | 'targetElementQuery' | 'targetType' | 'customCSSSelector'
  >,
  containerEl: HTMLElement,
): HTMLElement | null {
  // Try element query first if targetType is 'element'
  if (step.targetType === 'element' && step.targetElementQuery) {
    const element = resolveElementSelector(step.targetElementQuery, containerEl);
    if (element) return element;
  }

  // Try custom CSS selector
  if (step.customCSSSelector) {
    try {
      const element = containerEl.querySelector<HTMLElement>(step.customCSSSelector);
      if (element) return element;
    } catch {
      // Invalid selector, continue
    }
  }

  // Fallback to block ID
  if (step.targetBlockId) {
    return containerEl.querySelector<HTMLElement>(
      `[${BlockDataAttributes.BlockId}="${step.targetBlockId}"]`,
    );
  }

  return null;
}

/**
 * Check if step has any target configuration
 */
function hasTargetConfig(
  step: Pick<
    TooltipWalkthroughStep,
    'targetBlockId' | 'targetElementQuery' | 'customCSSSelector'
  >,
): boolean {
  return !!(step.targetBlockId || step.targetElementQuery || step.customCSSSelector);
}

// ============================================================================
// Target Element Finder Service
// ============================================================================

interface FinderState {
  retryTimeoutId: ReturnType<typeof setTimeout> | null;
  delayTimeoutId: ReturnType<typeof setTimeout> | null;
  observer: MutationObserver | null;
  currentStepId: string | null;
  isActive: boolean;
  retryDelayMs: number; // Dynamic retry delay based on context
}

interface FindTargetOptions {
  step: Pick<
    TooltipWalkthroughStep,
    | 'id'
    | 'targetBlockId'
    | 'targetElementQuery'
    | 'targetType'
    | 'customCSSSelector'
    | 'waitUntilElementAppears'
    | 'waitUntilElementAppearsDelay'
  >;
  containerEl: HTMLElement;
  getStore: () => WalkthroughManagerStore;
  onElementFound: (element: HTMLElement) => void;
  onElementMissing: () => void;
  elementMissingBehavior?: ElementMissingBehavior;
  isBuilderOrPreview?: boolean;
  moveStep?: (direction: 'next' | 'previous') => void;
  stopWalkthrough?: () => void;
}

/**
 * Service that manages target element finding with retry logic.
 * Lives outside React lifecycle - timers persist across re-renders.
 */
export class TargetElementFinder {
  private state: FinderState = {
    retryTimeoutId: null,
    delayTimeoutId: null,
    observer: null,
    currentStepId: null,
    isActive: false,
    retryDelayMs: RETRY_DELAY_RUNNER_MS, // Default to runner delay
  };

  /**
   * Start finding target element for a step
   */
  start(options: FindTargetOptions): void {
    const { step, isBuilderOrPreview = false } = options;

    // If same step, don't restart
    if (this.state.currentStepId === step.id && this.state.isActive) {
      return;
    }

    // Clean up previous search
    this.cleanup();

    this.state.currentStepId = step.id;
    this.state.isActive = true;
    // Set retry delay based on context: fast in builder, slower in runner
    this.state.retryDelayMs = isBuilderOrPreview ? RETRY_DELAY_BUILDER_MS : RETRY_DELAY_RUNNER_MS;

    // Start the search
    this.findElement(options);
  }

  /**
   * Stop current search and clean up
   */
  stop(): void {
    this.cleanup();
    this.state.currentStepId = null;
    this.state.isActive = false;
  }

  /**
   * Clean up timers and observers without resetting state
   */
  private cleanup(): void {
    if (this.state.retryTimeoutId) {
      clearTimeout(this.state.retryTimeoutId);
      this.state.retryTimeoutId = null;
    }
    if (this.state.delayTimeoutId) {
      clearTimeout(this.state.delayTimeoutId);
      this.state.delayTimeoutId = null;
    }
    if (this.state.observer) {
      this.state.observer.disconnect();
      this.state.observer = null;
    }
  }

  effectCleanup(): void {
    if (this.state.observer) {
      this.state.observer.disconnect();
      this.state.observer = null;
    }
    this.state.currentStepId = null;
    this.state.isActive = false;
  }

  /**
   * Core element finding logic with retry state machine
   */
  private findElement(options: FindTargetOptions): void {
    const {
      step,
      containerEl,
      getStore,
      onElementFound,
      onElementMissing,
      elementMissingBehavior,
      isBuilderOrPreview = false,
      moveStep,
      stopWalkthrough,
    } = options;

    // Guard: no target configuration
    if (!hasTargetConfig(step)) {
      this.stop();
      if (isBuilderOrPreview) {
        stopWalkthrough?.();
        onElementMissing();
      }
      return;
    }

    // Get current transition state
    const store = getStore();
    const transitionState = store.driverState.tooltipTransitionState;
    const transitionStatus = transitionState?.status;

    // Guard: preparing state
    if (transitionStatus === 'preparing') {
      this.stop();
      return;
    }

    const { waitUntilElementAppears, waitUntilElementAppearsDelay } = step;
    const transitionRetryCount = transitionState?.retryCount ?? 0;
    const transitionMaxRetries = transitionState?.maxRetries ?? 3;
    const transitionTargetStepIndex = transitionState?.targetStepIndex ?? -1;
    const isTransitionActive =
      transitionStatus === 'finding_element' ||
      transitionStatus === 'retrying' ||
      transitionStatus === 'scrolling';

    const element = resolveTarget(step, containerEl);

    if (element) {
      // Element found - handle transition and scrolling
      this.handleElementFound({
        element,
        containerEl,
        isTransitionActive,
        transitionStatus,
        transitionRetryCount,
        transitionMaxRetries,
        transitionTargetStepIndex,
        waitUntilElementAppears,
        waitUntilElementAppearsDelay,
        getStore,
        onElementFound,
      });
    } else {
      // Element not found - handle retry or missing
      this.handleElementNotFound({
        step,
        containerEl,
        isTransitionActive,
        transitionRetryCount,
        transitionMaxRetries,
        transitionTargetStepIndex,
        waitUntilElementAppears,
        elementMissingBehavior,
        isBuilderOrPreview,
        getStore,
        onElementMissing,
        moveStep,
        stopWalkthrough,
        options,
      });
    }
  }

  /**
   * Handle when element is found
   */
  private handleElementFound(params: {
    element: HTMLElement;
    containerEl: HTMLElement;
    isTransitionActive: boolean;
    transitionStatus: string | undefined;
    transitionRetryCount: number;
    transitionMaxRetries: number;
    transitionTargetStepIndex: number;
    waitUntilElementAppears: boolean | undefined;
    waitUntilElementAppearsDelay: number | undefined;
    getStore: () => WalkthroughManagerStore;
    onElementFound: (element: HTMLElement) => void;
  }): void {
    const {
      element,
      containerEl,
      isTransitionActive,
      transitionStatus,
      transitionRetryCount,
      transitionMaxRetries,
      transitionTargetStepIndex,
      waitUntilElementAppears,
      waitUntilElementAppearsDelay,
      getStore,
      onElementFound,
    } = params;

    const performScroll = async () => {
      const store = getStore();

      // Update transition state if in transition and not already scrolling
      if (isTransitionActive && transitionStatus !== 'scrolling') {
        store.driverActions.setTooltipTransitionState({
          status: 'scrolling',
          retryCount: transitionRetryCount,
          maxRetries: transitionMaxRetries,
          targetStepIndex: transitionTargetStepIndex,
        });
      }

      // Scroll element into view
      await scrollToElement(element, containerEl);

      // Update transition state to ready
      if (isTransitionActive) {
        store.driverActions.setTooltipTransitionState({
          status: 'ready',
          retryCount: transitionRetryCount,
          maxRetries: transitionMaxRetries,
          targetStepIndex: transitionTargetStepIndex,
        });
      }

      // Notify component
      onElementFound(element);

      // Disconnect observer if active
      if (this.state.observer) {
        this.state.observer.disconnect();
        this.state.observer = null;
      }

      // Commit display index if in active transition
      if (isTransitionActive) {
        store.driverActions.commitDisplayIndex();
      }

      // Mark as inactive
      this.state.isActive = false;
    };

    // Apply delay if specified and waiting for element
    if (waitUntilElementAppears && waitUntilElementAppearsDelay && waitUntilElementAppearsDelay > 0) {
      if (this.state.delayTimeoutId) {
        clearTimeout(this.state.delayTimeoutId);
      }
      this.state.delayTimeoutId = setTimeout(() => {
        void performScroll();
      }, waitUntilElementAppearsDelay);
    } else {
      void performScroll();
    }
  }

  /**
   * Handle when element is not found
   */
  private handleElementNotFound(params: {
    step: FindTargetOptions['step'];
    containerEl: HTMLElement;
    isTransitionActive: boolean;
    transitionRetryCount: number;
    transitionMaxRetries: number;
    transitionTargetStepIndex: number;
    waitUntilElementAppears: boolean | undefined;
    elementMissingBehavior?: ElementMissingBehavior;
    isBuilderOrPreview: boolean;
    getStore: () => WalkthroughManagerStore;
    onElementMissing: () => void;
    moveStep?: (direction: 'next' | 'previous') => void;
    stopWalkthrough?: () => void;
    options: FindTargetOptions;
  }): void {
    const {
      step,
      containerEl,
      isTransitionActive,
      transitionRetryCount,
      transitionMaxRetries,
      transitionTargetStepIndex,
      waitUntilElementAppears,
      elementMissingBehavior,
      isBuilderOrPreview,
      getStore,
      onElementMissing,
      moveStep,
      stopWalkthrough,
      options,
    } = params;

    if (isTransitionActive && transitionRetryCount < transitionMaxRetries) {
      // Retry logic - update state and schedule retry
      const store = getStore();
      store.driverActions.setTooltipTransitionState({
        status: 'retrying',
        retryCount: transitionRetryCount + 1,
        maxRetries: transitionMaxRetries,
        targetStepIndex: transitionTargetStepIndex,
      });

      // Schedule retry - timer lives in service, not React
      if (this.state.retryTimeoutId) {
        clearTimeout(this.state.retryTimeoutId);
      }
      this.state.retryTimeoutId = setTimeout(() => {
        this.findElement(options);
      }, this.state.retryDelayMs);
    } else if (isTransitionActive && transitionRetryCount >= transitionMaxRetries) {
      // Max retries reached
      this.handleMaxRetriesReached({
        elementMissingBehavior,
        isBuilderOrPreview,
        transitionMaxRetries,
        transitionTargetStepIndex,
        getStore,
        onElementMissing,
        moveStep,
        stopWalkthrough,
      });
    } else if (!waitUntilElementAppears) {
      // Not in transition and not waiting - handle missing immediately
      this.handleMaxRetriesReached({
        elementMissingBehavior,
        isBuilderOrPreview,
        transitionMaxRetries,
        transitionTargetStepIndex,
        getStore,
        onElementMissing,
        moveStep,
        stopWalkthrough,
      });
    } else {
      // Set up MutationObserver to wait for element
      this.setupObserver(containerEl, options);
    }
  }

  /**
   * Handle when max retries are reached or element is missing
   */
  private handleMaxRetriesReached(params: {
    elementMissingBehavior?: ElementMissingBehavior;
    isBuilderOrPreview: boolean;
    transitionMaxRetries: number;
    transitionTargetStepIndex: number;
    getStore: () => WalkthroughManagerStore;
    onElementMissing: () => void;
    moveStep?: (direction: 'next' | 'previous') => void;
    stopWalkthrough?: () => void;
  }): void {
    const {
      elementMissingBehavior,
      isBuilderOrPreview,
      transitionMaxRetries,
      transitionTargetStepIndex,
      getStore,
      onElementMissing,
      moveStep,
      stopWalkthrough,
    } = params;

    // Handle runtime behavior (skip/end)
    if (!isBuilderOrPreview && elementMissingBehavior) {
      if (elementMissingBehavior === 'skip') {
        moveStep?.('next');
      } else if (elementMissingBehavior === 'end') {
        stopWalkthrough?.();
      }
    }

    // Clear transition state
    const store = getStore();
    store.driverActions.setTooltipTransitionState({
      status: 'idle',
      retryCount: 0,
      maxRetries: transitionMaxRetries,
      targetStepIndex: transitionTargetStepIndex,
    });

    // Notify component
    if (isBuilderOrPreview) {
      stopWalkthrough?.();
      onElementMissing();
    }

    // Stop finder
    this.stop();
  }

  /**
   * Set up MutationObserver to watch for element appearance
   */
  private setupObserver(containerEl: HTMLElement, options: FindTargetOptions): void {
    if (this.state.observer) {
      this.state.observer.disconnect();
    }

    const observer = new MutationObserver(() => {
      this.findElement(options);
    });

    observer.observe(containerEl, {
      childList: true,
      subtree: true,
      attributes: false,
      characterData: false,
    });

    this.state.observer = observer;
  }
} 